<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';
import { markPwaBusy, markPwaIdle } from 'dashboard/composables/pwaReloadGuard';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
  height: {
    type: Number,
    default: 100,
  },
  // Só para WAV: reamostra antes de gerar o arquivo (ver `reamostrar`).
  // Sem valor, o áudio sai na taxa em que foi gravado.
  taxaDeAmostragem: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const hasRecording = ref(false);

// Guarda de reload do PWA (ver pwaReloadGuard.js) — cobre da gravação até
// alguns segundos depois do áudio convertido/emitido, janela em que o
// ReplyBox está subindo o arquivo. `pwaBusyActive` evita contar 2x se o
// componente desmontar antes do timeout disparar.
let pwaBusyActive = false;
const beginPwaBusy = () => {
  if (pwaBusyActive) return;
  pwaBusyActive = true;
  markPwaBusy();
};
const endPwaBusy = () => {
  if (!pwaBusyActive) return;
  pwaBusyActive = false;
  markPwaIdle();
};

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

// blue-9 e slate-9 da paleta advup, em hexadecimal porque o canvas do
// WaveSurfer nao enxerga variavel CSS.
const AZUL_DA_MARCA = '#2563eb';
const CINZA_DA_ONDA = '#8b8d98';

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    // A onda do audio nao pode ler token CSS (o WaveSurfer desenha em canvas,
    // fora do alcance da folha de estilo). Os dois valores eram do Chatwoot:
    // um azul que nao e o nosso e um cinza avulso. Aqui ficam os NOSSOS,
    // escritos por extenso porque e o unico jeito neste contexto.
    waveColor: AZUL_DA_MARCA,
    progressColor: CINZA_DA_ONDA,
    height: props.height,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('play', () => {
    // eslint-disable-next-line no-console
    console.log('[AR] WS play event fired');
    emit('play');
  });
  wavesurfer.value.on('pause', () => {
    // eslint-disable-next-line no-console
    console.log('[AR] WS pause event fired');
    emit('pause');
  });
  wavesurfer.value.on('finish', () => {
    // eslint-disable-next-line no-console
    console.log('[AR] WS finish event fired');
    // Reset media position directly (not via wavesurfer.setTime which redraws canvas)
    // so the next play() starts from 0 without causing a layout shift that eats clicks
    const media = wavesurfer.value.getMediaElement();
    if (media) media.currentTime = 0;
    emit('pause');
  });

  record.value = wavesurfer.value.plugins[0];

  record.value.on('record-end', async blob => {
    const audioBlob = await convertAudio(blob, props.audioRecordFormat, 128, {
      taxa: props.taxaDeAmostragem,
    });
    const audioUrl = URL.createObjectURL(audioBlob);
    const fileName = `${getUuid()}.mp3`;
    const file = new File([audioBlob], fileName, {
      type: props.audioRecordFormat,
    });

    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    isRecording.value = false;
    // Segura a guarda mais alguns segundos — tempo do ReplyBox iniciar o
    // upload de verdade, não só emitir o evento.
    setTimeout(endPwaBusy, 8000);

    try {
      await wavesurfer.value.load(audioUrl);
    } catch {
      // AbortError expected when component unmounts before load completes
    }

    hasRecording.value = true;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
  beginPwaBusy();
};

const play = () => {
  const media = wavesurfer.value?.getMediaElement();
  // eslint-disable-next-line no-console
  console.log('[AR] play() called', {
    hasRecording: hasRecording.value,
    ws: !!wavesurfer.value,
    duration: wavesurfer.value?.getDuration()?.toFixed(2),
    isPlaying: wavesurfer.value?.isPlaying(),
    mediaSrc: media?.src?.substring(0, 40),
    mediaReadyState: media?.readyState,
    mediaPaused: media?.paused,
    mediaEnded: media?.ended,
    mediaCurrentTime: media?.currentTime?.toFixed(2),
  });
  if (!hasRecording.value || !wavesurfer.value) return;
  wavesurfer.value
    .play()
    // eslint-disable-next-line no-console
    .then(() => console.log('[AR] wavesurfer.play() resolved'))
    // eslint-disable-next-line no-console
    .catch(err =>
      // eslint-disable-next-line no-console
      console.error('[AR] wavesurfer.play() REJECTED:', err.name, err.message)
    );
};

const pause = () => {
  // eslint-disable-next-line no-console
  console.log('[AR] pause() called', {
    ws: !!wavesurfer.value,
    isPlaying: wavesurfer.value?.isPlaying(),
  });
  if (!wavesurfer.value) return;
  wavesurfer.value.pause();
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    try {
      wavesurfer.value.destroy();
    } catch {
      // AbortError may occur when destroy() is called while an async
      // load operation is in progress (e.g. after recording ends)
    }
  }
  // Rede de segurança: se o componente sumir antes do timeout de 8s (ex:
  // usuário cancelou a gravação), libera a guarda na hora em vez de
  // deixar o PWA "preso" sem reload por até 8s à toa.
  endPwaBusy();
});

defineExpose({ play, pause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
